import React, { useState, useEffect, useRef, useCallback } from 'react';
import { HiMagnifyingGlass, HiXMark } from 'react-icons/hi2';
import './StockSearchBar.scss';
import { ProductStock } from '../../types';

interface StockSearchBarProps {
  data: ProductStock[];
  updateFilteredData: (filteredData: ProductStock[]) => void;
  placeholder?: string;
}

interface SuggestionItem {
  item: ProductStock;
  matchedField: 'name' | 'storage_unit_name' | 'acronym';
  matchedValue: string;
}

const removeAccents = (str: string): string => {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

const BADGE_COLORS: Record<string, string> = {
  name: 'var(--mediopunto-orange)',
  storage_unit_name: '#3b82f6',
  acronym: '#22c55e',
};

const StockSearchBar: React.FC<StockSearchBarProps> = ({
  data,
  updateFilteredData,
  placeholder = "Buscar..."
}) => {
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState<SuggestionItem[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter data based on input
  const filterData = useCallback((searchValue: string): ProductStock[] => {
    if (searchValue.trim() === '') {
      return data;
    }

    const normalizedInput = removeAccents(searchValue.toLowerCase());
    return data.filter(item => {
      const name = removeAccents((item.name || '').toLowerCase());
      const storage = removeAccents((item.storage_unit_name || '').toLowerCase());
      const acronym = removeAccents((item.acronym || '').toLowerCase());
      return name.includes(normalizedInput) || storage.includes(normalizedInput) || acronym.includes(normalizedInput);
    });
  }, [data]);

  // Generate unique suggestions (one per item, no duplicates)
  const generateSuggestions = useCallback((searchValue: string): SuggestionItem[] => {
    if (searchValue.trim() === '') {
      return [];
    }

    const normalizedInput = removeAccents(searchValue.toLowerCase());
    const matchedItems: SuggestionItem[] = [];
    const seenItemIds = new Set<string>();

    for (const item of data) {
      const uniqueId = `${item.product_id}-${item.storage_unit_id}`;
      if (seenItemIds.has(uniqueId)) continue;

      const name = removeAccents((item.name || '').toLowerCase());
      const storage = removeAccents((item.storage_unit_name || '').toLowerCase());
      const acronym = removeAccents((item.acronym || '').toLowerCase());

      if (name.includes(normalizedInput)) {
        matchedItems.push({ item, matchedField: 'name', matchedValue: item.name });
        seenItemIds.add(uniqueId);
      } else if (storage.includes(normalizedInput)) {
        matchedItems.push({ item, matchedField: 'storage_unit_name', matchedValue: item.storage_unit_name });
        seenItemIds.add(uniqueId);
      } else if (acronym.includes(normalizedInput)) {
        matchedItems.push({ item, matchedField: 'acronym', matchedValue: item.acronym });
        seenItemIds.add(uniqueId);
      }
    }

    return matchedItems.slice(0, 15);
  }, [data]);

  // Handle input change
  useEffect(() => {
    const filteredData = filterData(input);
    updateFilteredData(filteredData);

    const newSuggestions = generateSuggestions(input);
    setSuggestions(newSuggestions);
    setShowSuggestions(newSuggestions.length > 0 && input.trim() !== '');
    setHighlightedIndex(-1);
  }, [input, filterData, generateSuggestions, updateFilteredData]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
          selectSuggestion(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
    }
  };

  const selectSuggestion = (suggestion: SuggestionItem) => {
    setInput(suggestion.matchedValue);
    setShowSuggestions(false);
    updateFilteredData([suggestion.item]);
  };

  const clearInput = () => {
    setInput('');
    setSuggestions([]);
    setShowSuggestions(false);
    updateFilteredData(data);
    inputRef.current?.focus();
  };

  // Highlight matched text in suggestion
  const highlightMatch = (text: string, query: string): JSX.Element => {
    if (!query.trim()) return <>{text}</>;

    const normalizedText = removeAccents(text.toLowerCase());
    const normalizedQuery = removeAccents(query.toLowerCase());
    const startIndex = normalizedText.indexOf(normalizedQuery);

    if (startIndex === -1) return <>{text}</>;

    const beforeMatch = text.substring(0, startIndex);
    const match = text.substring(startIndex, startIndex + query.length);
    const afterMatch = text.substring(startIndex + query.length);

    return (
      <>
        {beforeMatch}
        <span style={{ background: 'rgba(252, 117, 86, 0.2)', color: 'var(--mediopunto-orange)', fontWeight: 600, padding: '0 2px', borderRadius: '2px' }}>{match}</span>
        {afterMatch}
      </>
    );
  };

  const getFieldLabel = (field: 'name' | 'storage_unit_name' | 'acronym'): string => {
    switch (field) {
      case 'name': return 'Producto';
      case 'storage_unit_name': return 'Almacén';
      case 'acronym': return 'Unidad';
      default: return '';
    }
  };

  return (
    <div className="stock-search-bar-container" ref={containerRef}>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <HiMagnifyingGlass
          className="w-4 h-4"
          style={{ position: 'absolute', left: '12px', color: 'var(--text-color)', opacity: 0.4, pointerEvents: 'none' }}
        />
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          autoComplete="off"
          className="stock-search-input"
          style={{
            width: '100%',
            height: '36px',
            paddingLeft: '36px',
            paddingRight: input ? '36px' : '12px',
            background: 'transparent',
            border: '1px solid var(--border-color)',
            borderRadius: showSuggestions ? '8px 8px 0 0' : '8px',
            color: 'var(--text-color)',
            fontSize: '14px',
            outline: 'none',
          }}
        />
        {input && (
          <button
            onClick={clearInput}
            type="button"
            aria-label="Limpiar búsqueda"
            style={{
              position: 'absolute',
              right: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent',
              border: 'none',
              color: 'var(--text-color)',
              opacity: 0.5,
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '50%',
            }}
          >
            <HiXMark className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="stock-suggestions-dropdown">
          <div style={{ display: 'flex', alignItems: 'center', padding: '8px 16px', borderBottom: '1px solid var(--border-color)' }}>
            <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-color)', opacity: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {suggestions.length} resultado{suggestions.length !== 1 ? 's' : ''}
            </span>
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: '6px', overflowY: 'auto', maxHeight: '280px' }}>
            {suggestions.map((suggestion, index) => (
              <li
                key={`${suggestion.item.product_id}-${suggestion.item.storage_unit_id}-${index}`}
                onClick={() => selectSuggestion(suggestion)}
                onMouseEnter={() => setHighlightedIndex(index)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  gap: '12px',
                  transition: 'background 0.15s ease',
                  background: index === highlightedIndex ? 'rgba(252, 117, 86, 0.08)' : 'transparent',
                }}
              >
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-color)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {highlightMatch(suggestion.item.name, input)}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '11px', color: 'var(--text-color)', opacity: 0.6 }}>
                      {suggestion.item.storage_unit_name}
                    </span>
                    {suggestion.item.acronym && (
                      <span style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text-color)', opacity: 0.5, background: 'rgba(128,128,128,0.1)', padding: '2px 6px', borderRadius: '4px' }}>
                        {suggestion.item.acronym}
                      </span>
                    )}
                  </div>
                </div>
                <span style={{
                  flexShrink: 0,
                  fontSize: '10px',
                  fontWeight: 600,
                  padding: '4px 8px',
                  borderRadius: '6px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px',
                  background: `${BADGE_COLORS[suggestion.matchedField]}1a`,
                  color: BADGE_COLORS[suggestion.matchedField],
                }}>
                  {getFieldLabel(suggestion.matchedField)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default StockSearchBar;
